import { useCallback, useMemo, useRef, useState } from "react";
import { type RenderConfigVideoClip } from "@memento/types/render-config";
import { clampRange } from "@memento/library/numbers";
import { toFrames } from "@memento/library/render";
import { TrimRange, trimRangeToDurationRange } from "src/types/clip-editor";
import { ClipSliders } from "src/components/ClipEditor/ClipSliders";
import constants from "src/constants";
import { secondsToTime } from "src/libs/cardUtils";
import { useEditor } from "src/components/Renderer/RendererProvider";

type EditVideoClipTrimProps = {
  clip: RenderConfigVideoClip;
};

export const EditVideoClipTrim = ({ clip }: EditVideoClipTrimProps) => {
  const {
    setClipTrim,
    setFrameToSeek,
    setPlayPauseRequest,
    fps,
    clipPrefetchDetailsBySegmentId,
    isRemotionPlayerPlaying,
  } = useEditor();

  const startFrame =
    clipPrefetchDetailsBySegmentId[clip.segmentId]?.startFrame ?? 0;
  const durationSeconds = clip.durationSeconds;
  const trimNormalized = clip.trimNormalized;

  // useMemo to stabilize the object reference
  const trimTimes = useMemo(
    () => ({
      min: trimNormalized.start * durationSeconds,
      max: trimNormalized.end * durationSeconds,
    }),
    [durationSeconds, trimNormalized.start, trimNormalized.end]
  );

  const [currentTime, setCurrentTime] = useState(
    () => trimNormalized.start * durationSeconds
  );

  const wasPlayingBeforeScrubbing = useRef({
    checked: false,
    wasPlaying: false,
  });

  const handleTrimSliderChange = useCallback(
    (value: TrimRange) => {
      if (
        value.max - value.min <
        constants.clipEditor.trim.MINIMUM_TRIM_LENGTH_SECONDS
      ) {
        return;
      }

      const maxValid =
        value.max < 0
          ? 0
          : value.max > durationSeconds
            ? durationSeconds
            : value.max;

      const minClamped = clampRange(value.min, { min: 0, max: maxValid });
      const maxClamped = clampRange(value.max, {
        min: minClamped,
        max: durationSeconds,
      });

      setClipTrim(
        clip.segmentId,
        trimRangeToDurationRange(
          { min: minClamped, max: maxClamped },
          durationSeconds
        )
      );

      if (!wasPlayingBeforeScrubbing.current.checked) {
        setPlayPauseRequest({ play: false });
        wasPlayingBeforeScrubbing.current = {
          checked: true,
          wasPlaying: isRemotionPlayerPlaying,
        };
      }
    },
    [
      durationSeconds,
      setClipTrim,
      clip.segmentId,
      setPlayPauseRequest,
      isRemotionPlayerPlaying,
    ]
  );

  const handleScrubberChange = useCallback(
    (value: number) => {
      const valueClamped = clampRange(value, trimTimes);

      const valueFrame = toFrames(valueClamped, fps);
      const trimStartFrame = toFrames(trimTimes.min, fps);

      setCurrentTime(valueClamped);

      if (!wasPlayingBeforeScrubbing.current.checked) {
        wasPlayingBeforeScrubbing.current = {
          checked: true,
          wasPlaying: isRemotionPlayerPlaying,
        };
      }

      // We need to get the frame associated with the scrubber value. One thing
      // to note is that the startFrame is now where trimTimes.min is (because
      // the player doesn't include the full clip in its frame count), but the
      // scrubber value is still relative to the start time of the clip. So we
      // need to subtract trimTimes.min from the scrubber value frames to get
      // the frame location to scrub to.
      setFrameToSeek({
        frame: startFrame + valueFrame - trimStartFrame,
        pause: true,
      });
    },
    [
      setCurrentTime,
      trimTimes,
      fps,
      startFrame,
      setFrameToSeek,
      isRemotionPlayerPlaying,
      wasPlayingBeforeScrubbing,
    ]
  );

  function handlePointerUp() {
    if (wasPlayingBeforeScrubbing.current.wasPlaying) {
      setPlayPauseRequest({ play: true });
    }
    wasPlayingBeforeScrubbing.current = { checked: false, wasPlaying: false };
  }

  return (
    <div className="flex flex-col gap-4 text-sm text-muted">
      <h2 className="font-futura text-xl text-gray-700">
        Trim ({secondsToTime(trimTimes.max - trimTimes.min)})
      </h2>
      <div className="flex w-full px-4 pb-8">
        <ClipSliders
          currentTime={currentTime}
          trimTimes={trimTimes}
          duration={durationSeconds}
          onScrubberChange={handleScrubberChange}
          onTrimSliderChange={handleTrimSliderChange}
          onChangeComplete={handlePointerUp}
          variant="vertical"
        />
      </div>
    </div>
  );
};
